import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, ArrowUpRight } from "@phosphor-icons/react";
import { FadeIn } from "../components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Generic Markdown-lite renderer.
 *
 * Supports:
 *   ## Heading 2
 *   ### Heading 3
 *   - bullet item  (or * bullet)
 *   **bold text**
 *   Regular paragraph text
 *   Numbered lists (1. item)
 *
 * No dependency on a hardcoded Set of headings — works for any content.
 */
function renderContent(content) {
  if (!content) return null;

  const lines = String(content).split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) {
      i++;
      continue;
    }

    // ## Heading 2
    if (trimmed.startsWith("## ")) {
      blocks.push({ type: "h2", text: trimmed.slice(3) });
      i++;
      continue;
    }

    // ### Heading 3
    if (trimmed.startsWith("### ")) {
      blocks.push({ type: "h3", text: trimmed.slice(4) });
      i++;
      continue;
    }

    // Bullet list (- or *)
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      const items = [];
      while (
        i < lines.length &&
        (lines[i].trim().startsWith("- ") || lines[i].trim().startsWith("* "))
      ) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push({ type: "bullets", items });
      continue;
    }

    // Numbered list (1. item)
    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push({ type: "numbered", items });
      continue;
    }

    // Regular paragraph — collect consecutive non-special lines
    const paraLines = [trimmed];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith("## ") &&
      !lines[i].trim().startsWith("### ") &&
      !lines[i].trim().startsWith("- ") &&
      !lines[i].trim().startsWith("* ") &&
      !/^\d+\.\s/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: "paragraph", text: paraLines.join(" ") });
  }

  return blocks.map((block, idx) => {
    switch (block.type) {
      case "h2":
        return (
          <h2
            key={idx}
            className="mt-12 mb-4 text-2xl md:text-3xl font-display font-semibold tracking-tight text-[#0b2e5e]"
          >
            {renderInline(block.text)}
          </h2>
        );
      case "h3":
        return (
          <h3
            key={idx}
            className="mt-8 mb-3 text-xl md:text-2xl font-display font-semibold tracking-tight text-[#0b2e5e]"
          >
            {renderInline(block.text)}
          </h3>
        );
      case "bullets":
        return (
          <ul key={idx} className="my-5 space-y-3 pl-0">
            {block.items.map((item, j) => (
              <li
                key={j}
                className="flex items-start gap-3 rounded-2xl border border-[#0b2e5e]/10 bg-white/55 p-4 md:p-5"
              >
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#e11d48] flex-shrink-0" />
                <span className="text-[#0b2e5e]/80">{renderInline(item)}</span>
              </li>
            ))}
          </ul>
        );
      case "numbered":
        return (
          <ol key={idx} className="my-5 space-y-3 pl-0">
            {block.items.map((item, j) => (
              <li
                key={j}
                className="flex items-start gap-4 rounded-2xl border border-[#0b2e5e]/10 bg-white/55 p-4 md:p-5"
              >
                <span className="flex-shrink-0 h-7 w-7 rounded-full bg-[#e11d48]/10 border border-[#e11d48]/30 flex items-center justify-center font-display text-sm text-[#e11d48] font-semibold">
                  {j + 1}
                </span>
                <span className="text-[#0b2e5e]/80 pt-0.5">{renderInline(item)}</span>
              </li>
            ))}
          </ol>
        );
      case "paragraph":
      default:
        return (
          <p key={idx} className="text-lg md:text-xl text-[#0b2e5e]/78 leading-relaxed">
            {renderInline(block.text)}
          </p>
        );
    }
  });
}

/**
 * Handle inline **bold** and plain text within a line.
 */
function renderInline(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-[#0b2e5e]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        setError(status === 404 ? "Post not found." : "Couldn't load this post. Try again later.");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center text-[#0b2e5e]/55"
        data-testid="blog-detail-loading"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-2 border-[#e11d48]/40 border-t-[#e11d48] animate-spin" />
          <span className="font-mono-mini">Loading post…</span>
        </div>
      </div>
    );

  if (error || !post)
    return (
      <div
        className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-6"
        data-testid="blog-detail-error"
      >
        <h2 className="font-display text-4xl text-[#0b2e5e]">Post not found</h2>
        <p className="text-[#0b2e5e]/55">{error}</p>
        <Link to="/blog" className="btn-ghost">
          <ArrowLeft weight="bold" /> Back to journal
        </Link>
      </div>
    );

  return (
    <article data-testid="blog-detail-page">
      {/* ── Header ── */}
      <section className="pt-36 pb-10 md:pt-44">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <FadeIn>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#0b2e5e]/55 hover:text-[#0b2e5e] mb-8 font-mono-mini transition-colors"
            >
              <ArrowLeft weight="bold" /> Back to journal
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <span className="tag">{post.category}</span>
              <span className="issue-stripe">{formatDate(post.published_at)}</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-[84px] tracking-[-0.02em] font-semibold leading-[0.95] text-[#0b2e5e]">
              {post.title}
            </h1>

            <div className="mt-8 flex items-center gap-5 text-[#0b2e5e]/55 text-sm font-mono-mini">
              <span>{post.author}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={14} /> {post.read_minutes} min read
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Cover image ── */}
      {post.cover_image && (
        <FadeIn>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="rounded-3xl overflow-hidden border border-[#0b2e5e]/12">
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-[280px] md:h-[480px] object-cover"
              />
            </div>
          </div>
        </FadeIn>
      )}

      {/* ── Content ── */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="space-y-6">{renderContent(post.content)}</div>
          </FadeIn>

          <div className="hr-grad my-16" />

          <FadeIn>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <Link to="/blog" className="btn-ghost">
                <ArrowLeft weight="bold" /> All posts
              </Link>
              <Link to="/contact" className="btn-primary" data-testid="blog-detail-cta-contact">
                Start a project <ArrowUpRight weight="bold" size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </article>
  );
}
